import styles from './index.module.css'

import React from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'
import type { WithTranslation } from '@disco/i18n/with-translation'
import { isPasswordStrengthError } from '@disco/utils/password'

import type { PasswordStrength } from 'types/business'
import type { HttpErrorType } from 'types/errors'

import WeakPassword from 'components/password/weak-password-error'
import ContactUsLink from 'components/ui/contact-us-link'

type TranslateFn = WithTranslation['t']

// Custom hook to check if React Router is available
const useReactRouterAvailable = () => {
  try {
    // Attempt to use a React Router hook
    useNavigate()
    // If no error is thrown, React Router is available
    return true
  } catch (error) {
    // If an error is thrown, React Router is not available
    return false
  }
}

const BrowserLink = ({
  to,
  children,
}: {
  to: string
  children?: React.ReactNode
}) => (
  <a href={to} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

const SupportEmailLink = ({ children }: { children?: React.ReactNode }) => (
  <a href="mailto:support@disco.ac" target="_blank" rel="noopener noreferrer">
    {children ?? 'support@disco.ac'}
  </a>
)

const SecurePasswordGuideLink = ({
  children,
}: {
  children?: React.ReactNode
}) => (
  <a
    target="_blank"
    rel="noopener noreferrer"
    href="https://support.disco.ac/home/q-how-do-i-choose-a-secure-password"
  >
    {children ?? 'Find out more.'}
  </a>
)

function explainServerLoginError(
  t: TranslateFn,
  errorOrigin: HttpErrorType | null | undefined,
  passwordStrength: PasswordStrength,
  redirectIfUrlExpired?: string,
  errorContext?: 'link',
  hasRouter?: boolean | null | undefined,
) {
  // TODO: refactor explainServerLoginError - too many arguments
  // Error.prototype.message is non-enumerable, so a spread alone silently drops
  // it and an error carrying nothing else renders as an empty box.
  const error: Partial<HttpErrorType> = {
    ...errorOrigin,
    message: errorOrigin?.message,
  }
  error.code = error.code?.replace(/(\['|'\])/g, '')
  // TODO: replace hasRouter with <BrowserRouter> from react-router v6
  const Link = hasRouter === false ? BrowserLink : RouterLink
  const resetPasswordLink = <Link to="/forgot-password" />
  let message: React.ReactNode = null
  let hasWorkaround = false

  if (error.message) {
    message = error.message // here it spews a bit overdetailed messages which are nice for dev/qa
    // and, in general, should not make it to end-user
    //
    // e.g.
    //
    //     [404] (cannot POST https://example.disco.ac/api/misspelled-url-or-server-down/ (404)): Empty response
    //
  }

  if (
    error.body &&
    typeof error.body === 'object' &&
    !Array.isArray(error.body) &&
    'detail' in error.body
  ) {
    message = isPasswordStrengthError(error.body.detail)
      ? error.code
      : error.body.detail
  }

  if (error.code) {
    if (error.code === 'OLD_PASSWORD_INCORRECT') {
      message = t(
        'accounts:theCurrentPasswordIsNotCorrect',
        'The current password is not correct.',
      )
    }

    if (error.code === 'ADD_TO_DISCO_ERROR_NO_DISCOS') {
      message = t(
        'accounts:couldNotFindAnyDiscosForThisEmail',
        'Could not find any DISCOs for this email',
      )
    }

    if (error.code === 'ADD_TO_DISCO_ERROR_ALREADY_ADDED') {
      message = (
        <span className={styles.loginErrorConfirm}>
          {t(
            'accounts:goodNewsThisPlaylistIsAlreadyInYourDisco',
            'Good news! This playlist is already in your DISCO.',
          )}
        </span>
      )
    }

    if (
      error.code === 'PASSWORD_INCORRECT' ||
      error.code === 'ADD_TO_DISCO_ERROR_UNAUTHORIZED'
    ) {
      message = <span>{error.message}</span>
    }

    if (error.code === 'breached_password') {
      message = (
        <span>
          {/* Children repeat the sentence so it still renders where no i18next
              instance exists (jest); `defaults` alone renders nothing there. */}
          <Trans
            t={t}
            i18nKey="accounts:breachedPasswordMustResetBeforeLogIn"
            defaults="Sorry, that password is not strong enough, and may have been used on other systems. You must <resetLink>reset your password</resetLink> with a new, stronger password before you can log in. <guideLink>Find out more.</guideLink>"
            components={{
              resetLink: resetPasswordLink,
              guideLink: <SecurePasswordGuideLink />,
            }}
          >
            Sorry, that password is not strong enough, and may have been used on
            other systems. You must{' '}
            <Link to="/forgot-password">reset your password</Link> with a new,
            stronger password before you can log in. <SecurePasswordGuideLink />
          </Trans>
        </span>
      )
    }

    if (error.code === 'LOGIN_LIMIT_EXCEEDED') {
      message = (
        <div className={styles.loginErrorLimitExceeded}>
          <span>
            <Trans
              t={t}
              i18nKey="accounts:accountLockedOutTooManyInvalidLogins"
              defaults="Your account is locked out due to too many invalid login attempts. <br/><br/>Please reset your password or <supportLink>contact support</supportLink> to regain access.<br/><br/>"
              components={{ supportLink: <ContactUsLink /> }}
            >
              Your account is locked out due to too many invalid login attempts.{' '}
              <br />
              <br />
              Please reset your password or{' '}
              <ContactUsLink>contact support</ContactUsLink> to regain access.
              <br />
              <br />
            </Trans>
          </span>
        </div>
      )
    }

    if (
      error.code === 'NEW_PASSWORD_TOO_WEAK' &&
      error.body &&
      typeof error.body === 'object' &&
      !Array.isArray(error.body) &&
      'detail' in error.body
    ) {
      message = (
        <WeakPassword
          passwordStrength={error.body.detail as PasswordStrength}
        />
      )
    }
  }

  let serverMessage = ''

  if (typeof error.body === 'string') {
    serverMessage = error.body
  } else if (Array.isArray(error.body) && typeof error.body[0] === 'string') {
    serverMessage = error.body[0]
  }

  if (serverMessage) {
    if (serverMessage === 'invalid referer URL') {
      message = t('accounts:sorryLinkIsInvalid', 'Sorry! Link is invalid.')
    } else if (serverMessage === 'invite has been deleted') {
      message = t(
        'accounts:sorryThisLinkIsNoLongerValid',
        'Sorry! This link is no longer valid.',
      )
    } else if (serverMessage === 'referer already used') {
      message = t(
        'accounts:sorryThisLinkHasBeenAlreadyBeenUsed',
        'Sorry, this link has been already been used.',
      )
    } else if (serverMessage === 'password is too weak') {
      message = <WeakPassword passwordStrength={passwordStrength} />
    } else if (
      serverMessage === 'referer URL has expired' &&
      redirectIfUrlExpired
    ) {
      hasWorkaround = true
      message = (
        <span>
          <Trans
            t={t}
            i18nKey="accounts:sorryTheLinkHasExpiredRequestANewLink"
            defaults="Sorry, the link has expired. <resetLink>Request a new link.</resetLink>"
            components={{ resetLink: resetPasswordLink }}
          >
            Sorry, the link has expired.{' '}
            <Link to="/forgot-password">Request a new link.</Link>
          </Trans>
        </span>
      )
    } else if (errorContext === 'link') {
      //handles "referer URL has expired"
      message = t(
        'accounts:sorryLinkIsInvalidWithReason',
        'Sorry! Link is invalid: {{reason}}.',
        { reason: serverMessage },
      )
    } else {
      message = t('accounts:errorWithReason', 'Error: {{reason}}.', {
        reason: serverMessage,
      })
    }
  }

  return {
    message:
      message ??
      t(
        'accounts:sorryAnErrorOccurredPleaseTryAgain',
        'Sorry, an error occurred. Please try again.',
      ),
    hasWorkaround,
  }
}

type Props = {
  suggestContactingSupport?: boolean
  redirectIfUrlExpired?: string
  errorContext?: 'link'
  error: HttpErrorType | null | undefined
  passwordStrength: PasswordStrength
}

const LoginError = ({
  suggestContactingSupport,
  redirectIfUrlExpired,
  errorContext,
  error,
  passwordStrength,
}: Props): React.ReactElement<'div'> => {
  const { t } = useTranslation('accounts')
  const hasRouter = useReactRouterAvailable()

  const _renderContactSupport = (hasWorkaround: boolean) => {
    if (!suggestContactingSupport) return

    if (hasWorkaround) {
      return (
        <div>
          <Trans
            t={t}
            i18nKey="accounts:stillHavingTroubleContactSupport"
            defaults="Still having trouble? Contact <supportEmail>support@disco.ac</supportEmail>."
            components={{ supportEmail: <SupportEmailLink /> }}
          >
            Still having trouble? Contact <SupportEmailLink />.
          </Trans>
        </div>
      )
    } else {
      return (
        <div>
          <Trans
            t={t}
            i18nKey="accounts:ifYouThinkYoureReceivingThisMessageInErrorPleaseContactSupport"
            defaults="If you think you’re receiving this message in error, please contact <supportEmail>support@disco.ac</supportEmail>."
            components={{ supportEmail: <SupportEmailLink /> }}
          >
            If you think you’re receiving this message in error, please contact{' '}
            <SupportEmailLink />.
          </Trans>
        </div>
      )
    }
  }

  const { message, hasWorkaround } = explainServerLoginError(
    t,
    error,
    passwordStrength,
    redirectIfUrlExpired,
    errorContext,
    hasRouter,
  )
  return (
    <div className={styles.loginErrorContainer}>
      {message}
      {_renderContactSupport(hasWorkaround)}
    </div>
  )
}

export default LoginError
